import { Outlet, Link, useLocation } from 'react-router-dom';
import { ChevronDown, CircleUser, Menu } from 'lucide-react';
import { ErrorBoundary } from '@/components/error-boundary';
import { logout } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { NavLink } from './ui/navlink';
import { Logo } from './logo';

/** Admin-only destinations, grouped under the "Admin" menu.
 *  `external: true` = static page under public/, not a SPA route. */
const ADMIN_LINKS = [
  { to: '/stats', text: 'Box Stats' },
  { to: '/box-history', text: 'Box History' },
  { to: '/incidents', text: 'Incident Log' },
  { to: '/apks', text: 'APKs' },
  { to: '/stb-stats', text: 'Usage Stats', external: true },
];

export function DashboardMenu({ authorization }: any) {
  const location = useLocation();
  return (
    <div className="flex min-h-screen w-full flex-col">
      <div className="sticky top-0 z-50">
        <header className="flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6">
          <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
            <Link
              to="/"
              className="flex items-center gap-2 text-lg font-semibold md:text-base"
            >
              <Logo />
              <span className="sr-only">Acme Inc</span>
            </Link>
            <NavLink to="/" text="Dashboard" />
            {authorization.boxes && <NavLink to="/boxes" text="Boxes" />}
            {(authorization.boxes || authorization.admin) && (
              <NavLink to="/monitoring" text="Monitoring" />
            )}
            {authorization.tvController && (
              <>
                <NavLink to="/tv-controllers" text="ZeeVee Dashboard" />
                <NavLink to="/images" text="Images" />
                {/* <NavLink to="/presets" text="Presets" /> */}
              </>
            )}
            {authorization.channelGuide && (
              <NavLink to="/channel-guides" text="Channel guide" />
            )}
            {authorization.admin && (
              <>
                {/* Tablets is admin-only for now (revert to tvController once
                    the feature is ready for suite staff). */}
                <NavLink to="/tablets" text="Tablets" />
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center gap-1 text-muted-foreground outline-hidden hover:text-foreground">
                    Admin
                    <ChevronDown className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {ADMIN_LINKS.map((link) =>
                      link.external ? (
                        <DropdownMenuItem key={link.to} asChild>
                          <a href={link.to}>{link.text}</a>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem key={link.to} asChild>
                          <Link to={link.to}>{link.text}</Link>
                        </DropdownMenuItem>
                      )
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </nav>
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0 md:hidden"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left">
              <nav className="grid gap-6 text-lg font-medium">
                <Link
                  to="/"
                  className="flex items-center gap-2 text-lg font-semibold"
                >
                  <Logo />
                  <span className="sr-only">Acme Inc</span>
                </Link>
                <NavLink to="/" text="Dashboard" />
                {authorization.boxes && <NavLink to="/boxes" text="Boxes" />}
                {(authorization.boxes || authorization.admin) && (
                  <NavLink to="/monitoring" text="Monitoring" />
                )}
                {authorization.tvController && (
                  <>
                    <NavLink to="/tv-controllers" text="ZeeVee Dashboard" />
                    <NavLink to="/images" text="Images" />
                    {/* <NavLink to="/presets" text="Presets" /> */}
                  </>
                )}
                {authorization.channelGuide && (
                  <NavLink to="/channel-guides" text="Channel guide" />
                )}
                {authorization.admin && (
                  <>
                    <NavLink to="/tablets" text="Tablets" />
                    {/* No dropdown inside the sheet — a labelled, indented
                        group reads better on a phone. */}
                    <div className="grid gap-4">
                      <span className="text-sm font-semibold text-muted-foreground">
                        Admin
                      </span>
                      <div className="grid gap-4 pl-4">
                        {ADMIN_LINKS.map((link) =>
                          link.external ? (
                            <a
                              key={link.to}
                              href={link.to}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              {link.text}
                            </a>
                          ) : (
                            <NavLink
                              key={link.to}
                              to={link.to}
                              text={link.text}
                            />
                          )
                        )}
                      </div>
                    </div>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-4 md:ml-auto md:gap-2 lg:gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  className="rounded-full"
                >
                  <CircleUser className="h-5 w-5" />
                  <span className="sr-only">Toggle user menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <Link to="/settings/notification">
                  <DropdownMenuItem>Settings</DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()}>
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
      </div>
      <main className="container mt-4">
        {/* Keyed on the path so navigating away from a crashed page resets it. */}
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
